import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { HiClock } from "react-icons/hi";
import { UITicket } from "../../../utils/eventHelpers";
import { StatusBadge } from "../../atoms/StatusBadge";

interface TicketPreviewProps {
  ticket: UITicket;
}

interface FormattedDateInfo {
  startDate: string | null;
  endDate: string | null;
  isStartInFuture: boolean;
  isEndInPast: boolean;
}

const formatTicketDate = (dateString: string): string =>
  moment(dateString).format("lll");

const useDateInfo = (
  saleStartDate?: string,
  saleEndDate?: string
): FormattedDateInfo | null => {
  return useMemo(() => {
    if (!saleStartDate && !saleEndDate) return null;

    const now = new Date();
    const startDate = saleStartDate ? new Date(saleStartDate) : null;
    const endDate = saleEndDate ? new Date(saleEndDate) : null;

    return {
      startDate: saleStartDate ? formatTicketDate(saleStartDate) : null,
      endDate: saleEndDate ? formatTicketDate(saleEndDate) : null,
      isStartInFuture: startDate ? startDate > now : false,
      isEndInPast: endDate ? endDate < now : false,
    };
  }, [saleStartDate, saleEndDate]);
};

const SALE_END_WARNING_DAYS = 3;

const TicketPreview: React.FC<TicketPreviewProps> = ({ ticket }) => {
  const { t } = useTranslation();
  const dateInfo = useDateInfo(ticket.sale_start_date, ticket.sale_end_date);

  const soldOut = useMemo(() => {
    if (!ticket.quantity_total) return false;
    return ticket.quantity_total - (ticket.quantity_sold || 0) <= 0;
  }, [ticket.quantity_total, ticket.quantity_sold]);

  const daysBeforeSaleEnd = useMemo(() => {
    if (soldOut || !ticket.sale_end_date) return null;
    const days = moment(ticket.sale_end_date)
      .startOf("day")
      .diff(moment().startOf("day"), "days");
    return days >= 0 && days <= SALE_END_WARNING_DAYS ? days : null;
  }, [soldOut, ticket.sale_end_date]);

  const availabilityText = useMemo(() => {
    if (!ticket.quantity_total) {
      return t("components.eventPayments.eventCard.unlimitedAvailability");
    }
    return t("components.eventPayments.eventCard.availability", {
      remaining: ticket.quantity_total - (ticket.quantity_sold || 0),
      total: ticket.quantity_total,
    });
  }, [ticket.quantity_total, ticket.quantity_sold, t]);

  const priceDisplay = useMemo(() => {
    if (ticket.price === 0) {
      return (
        <span className="text-green-700">
          {t("components.eventPayments.eventCard.free")}
        </span>
      );
    }
    return `${ticket.price} ${ticket.currency}`;
  }, [ticket.price, ticket.currency, t]);

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0 truncate text-sm font-medium text-gray-900">
          {ticket.name}
        </div>
        <div className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">
          {priceDisplay}
        </div>
      </div>

      <div className="mt-1 flex items-baseline justify-between gap-3 text-xs text-gray-500">
        <span className="truncate">
          {soldOut ? (
            <StatusBadge color="warning" className="inline-flex">
              {t("components.eventPayments.eventCard.soldOut")}
            </StatusBadge>
          ) : (
            availabilityText
          )}
        </span>
        {ticket.quantity_sold !== undefined && (
          <span className="shrink-0 tabular-nums">
            {t("components.eventPayments.eventCard.soldCount", {
              count: ticket.quantity_sold,
            })}
          </span>
        )}
      </div>

      {daysBeforeSaleEnd !== null && (
        <div className="mt-1.5">
          <StatusBadge color="warning" className="inline-flex">
            {daysBeforeSaleEnd === 0
              ? t("components.eventPayments.eventCard.saleEndsToday")
              : daysBeforeSaleEnd === 1
              ? t("components.eventPayments.eventCard.saleEndsTomorrow")
              : t("components.eventPayments.eventCard.saleEndsInDays", {
                  days: daysBeforeSaleEnd,
                })}
          </StatusBadge>
        </div>
      )}

      {dateInfo && (
        <div className="mt-2 flex items-center gap-1 border-t border-gray-200 pt-2 text-xs text-gray-500">
          <HiClock
            size={12}
            className="shrink-0 text-gray-400"
            aria-hidden="true"
          />
          <span className="truncate">
            {dateInfo.startDate && (
              <span
                className={
                  dateInfo.isStartInFuture ? "font-medium text-amber-700" : ""
                }
              >
                {dateInfo.startDate}
              </span>
            )}
            {dateInfo.startDate && dateInfo.endDate && (
              <span className="mx-1" aria-hidden="true">
                →
              </span>
            )}
            {dateInfo.endDate && (
              <span
                className={
                  dateInfo.isEndInPast ? "font-medium text-amber-700" : ""
                }
              >
                {dateInfo.endDate}
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default memo(TicketPreview);
